import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Globe, ArrowRight, Loader2, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "Cybersécurité",
  "Data & PostgreSQL",
  "Infrastructure & Cloud",
  "Intégration de systèmes",
  "Conseil IT",
  "Innovation",
  "Autre demande",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Votre demande a bien été enregistrée. Nous revenons vers vous sous 24h.");
      setForm({ name: "", email: "", company: "", phone: "", service: "", message: "", website: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Une erreur est survenue.";
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(" · ") : detail;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-32 bg-[#0A1326] relative"
    >
      <div className="absolute inset-0 peul-pattern opacity-25 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-5 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-12">
          <div className="lg:col-span-5">
            <div className="overline text-[#D4AF37]">── Parlons-en</div>
            <h2 className="font-display text-[32px] sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-tight mt-4 text-[#F5F5F5] leading-[1.05]">
              Bâtissons{" "}
              <span className="text-[#D4AF37]">ensemble</span> votre prochain
              chapitre digital.
            </h2>
            <p className="mt-6 text-[#F5F5F5]/65 text-[15px] md:text-base leading-relaxed max-w-md">
              Notre équipe avant-vente répond à toute sollicitation sous 24
              heures ouvrées. Diagnostic initial offert pour les structures de
              plus de 25 collaborateurs.
            </p>

            <div className="mt-10 space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-[#D4AF37]/30 inline-flex items-center justify-center rounded-[2px] text-[#D4AF37] shrink-0">
                  <Mail size={18} />
                </div>
                <div>
                  <div className="overline text-[#F5F5F5]/50">Email</div>
                  <a
                    href="mailto:contact@hakkilotechnologie.com"
                    data-testid="contact-email-link"
                    className="font-display text-lg md:text-xl font-bold mt-1 block text-[#F5F5F5] hover:text-[#D4AF37] transition-colors"
                  >
                    contact@hakkilotechnologie.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-[#D4AF37]/30 inline-flex items-center justify-center rounded-[2px] text-[#D4AF37] shrink-0">
                  <Phone size={18} />
                </div>
                <div>
                  <div className="overline text-[#F5F5F5]/50">Téléphone</div>
                  <a
                    href="tel:+2250789880088"
                    className="font-display text-lg md:text-xl font-bold mt-1 block text-[#F5F5F5] hover:text-[#D4AF37] transition-colors"
                  >
                    +225 07 89 88 00 88
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-[#D4AF37]/30 inline-flex items-center justify-center rounded-[2px] text-[#D4AF37] shrink-0">
                  <MapPin size={18} />
                </div>
                <div>
                  <div className="overline text-[#F5F5F5]/50">Siège social</div>
                  <div className="font-display text-lg md:text-xl font-bold mt-1 text-[#F5F5F5]">
                    Riviera Mbadon, face Chefferie
                  </div>
                  <div className="text-sm text-[#F5F5F5]/55 mt-1">
                    Abidjan, Côte d'Ivoire
                  </div>
                  <div className="text-xs text-[#D4AF37]/80 mt-2 tracking-[0.18em] uppercase font-semibold">
                    Présent à Abidjan · Dakar · Paris
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-[#D4AF37]/30 inline-flex items-center justify-center rounded-[2px] text-[#D4AF37] shrink-0">
                  <Globe size={18} />
                </div>
                <div>
                  <div className="overline text-[#F5F5F5]/50">Web</div>
                  <a
                    href="https://www.hakkilotechnologie.com"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="contact-website-link"
                    className="font-display text-lg md:text-xl font-bold mt-1 block text-[#F5F5F5] hover:text-[#D4AF37] transition-colors"
                  >
                    www.hakkilotechnologie.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-[#142042]/60 border border-[#D4AF37]/20 p-5 sm:p-7 md:p-10 rounded-[2px] relative"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="overline text-[#F5F5F5]/65 mb-2 block">
                    Nom complet *
                  </label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    minLength={2}
                    data-testid="contact-input-name"
                    className="hk-input"
                    placeholder="Aboubacar Touré"
                  />
                </div>
                <div>
                  <label className="overline text-[#F5F5F5]/65 mb-2 block">
                    Email professionnel *
                  </label>
                  <input
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    type="email"
                    data-testid="contact-input-email"
                    className="hk-input"
                    placeholder="vous@entreprise.ci"
                  />
                </div>
                <div>
                  <label className="overline text-[#F5F5F5]/65 mb-2 block">
                    Entreprise
                  </label>
                  <input
                    name="company"
                    value={form.company}
                    onChange={onChange}
                    data-testid="contact-input-company"
                    className="hk-input"
                    placeholder="Nom de votre structure"
                  />
                </div>
                <div>
                  <label className="overline text-[#F5F5F5]/65 mb-2 block">
                    Téléphone
                  </label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    data-testid="contact-input-phone"
                    className="hk-input"
                    placeholder="+225 ..."
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="overline text-[#F5F5F5]/65 mb-2 block">
                    Expertise concernée
                  </label>
                  <select
                    name="service"
                    value={form.service}
                    onChange={onChange}
                    data-testid="contact-select-service"
                    className="hk-input"
                  >
                    <option value="">— Choisir un domaine —</option>
                    {SERVICES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="overline text-[#F5F5F5]/65 mb-2 block">
                    Votre message *
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    required
                    minLength={5}
                    rows={5}
                    data-testid="contact-textarea-message"
                    className="hk-input resize-none"
                    placeholder="Décrivez brièvement votre besoin..."
                  />
                </div>
              </div>

              {/* Honeypot — hidden from real users, bots will fill it */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  width: 1,
                  height: 1,
                  overflow: "hidden",
                }}
              >
                <label>
                  Website (laissez vide)
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={onChange}
                  />
                </label>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="beam-btn inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1326] px-7 py-4 font-bold uppercase tracking-[0.16em] text-sm rounded-[2px] disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Envoi en cours…
                    </>
                  ) : done ? (
                    <>
                      <Check size={18} />
                      Demande envoyée
                    </>
                  ) : (
                    <>
                      Envoyer ma demande
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
                <p className="text-xs text-[#F5F5F5]/55 leading-relaxed">
                  En soumettant ce formulaire, vous acceptez que vos données
                  soient utilisées exclusivement pour traiter votre demande.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
